// Printer check: status-bar pill + popover listing checkLabel() issues. Clicking an issue selects its field.
import { checkLabel } from '../validate.js';
import { isSupportedFormat } from '../itf/dates.js';
import * as schema from '../itf/schema.js';
import { S, emit, subscribe, select, field } from './state.js';
import { $, h, debounce } from './dom.js';
import { icon } from './icons.js';
import { scrollToField } from './view.js';
import { displayText } from './geometry.js';

let panel = null;

/** Options for validate.checkLabel: real date previews for bounds, format + barcode verification flags. */
export const checkOptions = () => ({
  textFor: (f) => displayText(f),
  isSupportedFormat,
  barcodeTemplateVerified: schema.BARCODE_TEMPLATE_VERIFIED,
});

export function checkNow() {
  return S.label ? checkLabel(S.label, S.fields, checkOptions()) || [] : [];
}

export function runCheck() {
  if (!S.label) {
    S.issues = [];
  } else {
    try {
      S.issues = checkNow();
    } catch (err) {
      console.error('checkLabel failed', err);
      S.issues = [{ severity: 'warning', code: 'check-failed', message: `Printer check could not run: ${err.message}` }];
    }
  }
  emit('issues');
  return S.issues;
}
const runSoon = debounce(runCheck, 180);

export const countIssues = (list = S.issues) => ({
  errors: list.filter((i) => i.severity === 'error').length,
  warnings: list.filter((i) => i.severity !== 'error').length,
});

export function issuesFor(key) {
  return S.issues.filter((i) => i.fieldKey === key);
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function renderPill() {
  const pill = $('#check-pill');
  pill.hidden = !S.label;
  if (!S.label) return;
  const { errors, warnings } = countIssues();
  pill.classList.remove('good', 'warn', 'bad');
  if (!errors && !warnings) {
    pill.classList.add('good');
    pill.textContent = '✓ Ready to print';
  } else {
    pill.classList.add(errors ? 'bad' : 'warn');
    pill.textContent = [errors && plural(errors, 'error'), warnings && plural(warnings, 'warning')].filter(Boolean).join(' · ');
  }
  pill.title = 'Printer check — click for details';
  pill.setAttribute('aria-label', `Printer check: ${pill.textContent}. Show details`);
}

function renderPanel() {
  if (!panel) return;
  const list = h('ul', { class: 'check-list', role: 'list' });
  const sorted = [...S.issues].sort((a, b) => (a.severity === 'error' ? 0 : 1) - (b.severity === 'error' ? 0 : 1));
  if (!sorted.length) {
    list.appendChild(
      h('li', { class: 'check-item ok' }, h('span', { class: 'check-icon' }, icon('check', '')), h('span', { class: 'check-msg' }, 'Ready to print — no problems found.'), h('span')),
    );
  }
  for (const issue of sorted) {
    const f = issue.fieldKey ? field(issue.fieldKey) : null;
    const cls = issue.severity === 'error' ? 'error' : 'warning';
    const go = () => {
      if (!f) return;
      select([f.key]);
      scrollToField(f.key);
    };
    list.appendChild(
      h(
        'li',
        {
          class: `check-item ${cls}${f ? ' clickable' : ''}`,
          role: f ? 'button' : null,
          tabindex: f ? '0' : null,
          title: f ? `Select ${f.name}` : null,
          onClick: go,
          onKeydown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              go();
            }
          },
        },
        h('span', { class: 'check-icon', 'aria-label': cls }, icon(cls === 'error' ? 'error' : 'warning', '')),
        h('span', { class: 'check-msg' }, issue.message, issue.code ? h('span', { class: 'check-detail' }, issue.code) : null),
        h('span', { class: 'check-field' }, f ? f.name : ''),
      ),
    );
  }
  const { errors, warnings } = countIssues();
  panel.replaceChildren(
    h(
      'div',
      { class: 'menu-section', style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
      h('span', { id: 'check-title' }, 'Printer check'),
      h('span', null, errors || warnings ? `${plural(errors, 'error')} · ${plural(warnings, 'warning')}` : 'SmartDate X65'),
    ),
    h('div', { style: { padding: '4px', maxHeight: '50vh', overflowY: 'auto' } }, list),
  );
}

export function openCheckPanel() {
  if (panel || !S.label) return;
  runCheck();
  const pill = $('#check-pill');
  const r = pill.getBoundingClientRect();
  panel = h('div', {
    class: 'menu',
    role: 'dialog',
    'aria-labelledby': 'check-title',
    style: { position: 'fixed', right: `${Math.max(8, window.innerWidth - r.right)}px`, bottom: `${window.innerHeight - r.top + 6}px`, width: 'min(380px, calc(100vw - 16px))' },
  });
  renderPanel();
  document.body.appendChild(panel);
  pill.setAttribute('aria-expanded', 'true');
  const first = panel.querySelector('[tabindex="0"]');
  if (first) first.focus();
  setTimeout(() => document.addEventListener('mousedown', outside, true), 0);
  document.addEventListener('keydown', onKey, true);
}

export function closeCheckPanel() {
  if (!panel) return;
  panel.remove();
  panel = null;
  $('#check-pill').setAttribute('aria-expanded', 'false');
  document.removeEventListener('mousedown', outside, true);
  document.removeEventListener('keydown', onKey, true);
}
export const toggleCheckPanel = () => (panel ? closeCheckPanel() : openCheckPanel());

function outside(e) {
  if (panel && !panel.contains(e.target) && e.target !== $('#check-pill')) closeCheckPanel();
}
function onKey(e) {
  if (e.key === 'Escape') {
    e.stopPropagation();
    closeCheckPanel();
    $('#check-pill').focus();
  }
}

export function initCheck() {
  $('#check-pill').addEventListener('click', toggleCheckPanel);
  let checkedLabel = null;
  subscribe((r) => {
    if (r.has('doc')) {
      // A newly opened (or undo-restored) label is checked at once; edits are debounced.
      if (!S.label || S.label !== checkedLabel) {
        checkedLabel = S.label;
        runSoon.flush ? runSoon.flush() : runCheck();
      } else runSoon();
    }
    if (r.has('issues') || r.has('doc')) {
      renderPill();
      renderPanel();
    }
    if (!S.label) closeCheckPanel();
  });
}
